(ns leg.settings
  "Everything about this account that is not reading: who is signed in, how
  the window looks, which key does what, where the feeds come from and go,
  and the way out.

  A page and not a dialog (`/settings`, see `leg.route`). It was a dialog
  while it was three switches; it is a page now because adding a feed and
  taking the whole collection out again are done here, and those are work -
  something with a field to type in and an answer to read is not something
  to do through a window laid over the article somebody was reading.

  Every switch on it saves on the press. There is no Save button and this is
  not the place to invent one: the server answers with the settings it
  stored, and that answer is what the page then shows."
  (:require-macros [kc.core :refer [def-ui $]])
  (:require [clojure.string :as str]
            [kc.core :as kc]
            [leg.keys :as keys]
            [leg.mui :as mui :refer [sx]]
            [leg.page :as page]
            [leg.state :as state]
            [leg.theme :as theme]
            [leg.util :as u]))

;;; ------------------------------------------------------------------ keys

(defn- shown
  "A key as it is written on a keyboard. Most of them are the character
  itself; the ones that are a word are already a word, except the one that
  arrives as a blank."
  [k]
  (if (= " " k) "Space" (str k)))

(def-ui Key
  "One binding, as a field that takes a key instead of text. Press it and
  press a key: what is stored is what the keyboard sent, so a layout this
  program has never heard of binds the key that is actually under the
  finger.

  Read-only rather than a button that listens on the window: a field says
  \"this is where the next key goes\" with the focus ring alone, escape
  leaves it as it was, and tab still walks out of it."
  [label value on-set]
  ($ mui/TextField
     {:label label
      :value (shown value)
      :size "small"
      :sx (sx {:width "9.5rem"})
      :slotProps (sx {:htmlInput {:readOnly true :spellCheck false
                                  :style {:textAlign "center"}}})
      :onKeyDown (fn [^js e]
                   ;; Tab is how somebody leaves the field, a modifier on its
                   ;; own is not a binding, and backspace is the way back out
                   ;; of everything (`leg.keys`) - binding it here would be
                   ;; two things on one key. Everything else is fair game.
                   (when-not (contains? #{"Tab" "Backspace" "Shift" "Control" "Alt" "Meta"}
                                        (.-key e))
                     (.preventDefault e)
                     (when-not (= "Escape" (.-key e))
                       (on-set (.-key e)))))}))

(def-ui Keys
  "Which key does what, for whoever has a keyboard. Shown to everybody and
  not only to a window that looks like a laptop: a phone with a keyboard
  paired to it is steered by these keys as well, and a settings page that
  hides what a device can do until it guesses right is worse than four
  fields nobody on a phone will read."
  [settings]
  (let [bs (keys/bindings settings)]
    ($ page/Section {:title "Keys"}
       ($ mui/Typography {:sx (sx {:color "text.secondary" :fontSize ".85rem"})}
          "Up and down walk the list and scroll the article; the other two step one article along. Click a field and press the key you want.")
       ($ mui/Box {:sx (sx {:display "flex" :flexWrap "wrap" :gap ".5rem"
                            :marginTop ".5rem"})}
          (for [[action label] keys/actions]
            ($ Key {:key (name action)
                    :label label
                    :value (get bs action)
                    :on-set (fn [k]
                              (state/save-settings! {:reader/keys (assoc bs action k)}))})))
       ;; Two actions on one key is not refused - it is somebody halfway
       ;; through changing all four - but it is said, because the one that
       ;; happens is not the one they will expect.
       (when-not (= (count keys/actions) (count (set (vals bs))))
         ($ mui/Alert {:severity "warning" :variant "outlined"
                       :sx (sx {:marginTop ".5rem"})}
            "Two of these are on the same key."))
       ($ mui/Button {:sx (sx {:marginTop ".25rem"})
                      :disabled (= keys/defaults bs)
                      :onClick (fn [_] (state/save-settings! {:reader/keys {}}))}
          "Back to j k h l"))))

;;; ------------------------------------------------------------ appearance

(def-ui Choice
  "One of a handful of words, as a row of buttons. Not a select: two or
  three short answers are read faster side by side than they are found in a
  list that has to be opened first, and each of them is one press."
  [value options on-pick]
  ($ mui/Box {:sx (sx {:display "flex" :gap ".25rem"})}
     (for [[k label] options]
       ($ mui/Button {:key k
                      :onClick (fn [_] (on-pick k))
                      :color (if (= k value) "primary" "inherit")
                      :sx (sx (when-not (= k value) {:color "text.secondary"}))}
          label))))

;;; ----------------------------------------------------------------- feeds

(def-ui AddFeed
  "Subscribing, as one button: the field is in the dialog it opens
  (`leg.dialogs/AddFeed`). Typing an address is a job with an end, and a job
  with an end is a form that opens and closes."
  [busy?]
  ($ page/Section {:title "Add feed"}
     ($ mui/Button {:variant "contained" :disableElevation true
                    :disabled busy?
                    :onClick (fn [_] (state/dialog! :add-feed))}
        "Add feed")
     (when busy?
       ($ mui/Typography {:sx (sx {:color "text.secondary" :fontSize ".85rem"
                                   :marginTop ".35rem"})}
          "Fetching the feed..."))))

(def-ui FeedRow
  "One subscription, as a row: what it is called here, which group it is in,
  the address it comes from, and the way into the form that changes those
  facts about it (`leg.dialogs/Feed` - renaming, grouping, and removing).

  The whole row is the button, because the whole row is the feed. What is
  shown is what tells them apart: the name this reader gave it, and under it
  the address, when it was last fetched, and whatever went wrong last time.

  The group is beside the name, because this is the one page where the
  collection is looked at as a collection - the sidebar shows a group as a
  way in and says nothing about which feeds are in it, so without this
  there would be nowhere to see that a feed was filed in the wrong one."
  [feed]
  ($ mui/Box
     {:component "button"
      :type "button"
      :onClick (fn [_] (state/dialog! [:feed (:feed/id feed)]))
      :sx (sx {:display "block" :width "100%" :textAlign "left"
               :font "inherit" :color "inherit"
               :background "none" :border "none"
               :borderBottom "1px solid" :borderColor "divider"
               :padding ".45rem .25rem"
               :cursor "pointer"
               "&:hover" {:backgroundColor "action.hover"}})}
     ($ mui/Box {:sx (sx {:display "flex" :alignItems "baseline" :gap ".5rem"})}
        ($ mui/Box {:sx (sx {:fontWeight 600})}
           (or (not-empty (str (:feed/title feed))) (u/host (:feed/url feed))))
        (when-let [g (not-empty (str (:feed/group feed)))]
          ($ mui/Typography {:sx (sx {:flex "none" :color "text.secondary"
                                      :fontSize ".7rem" :textTransform "uppercase"
                                      :letterSpacing ".08em"})}
             g)))
     ($ mui/Typography {:sx (sx {:color "text.secondary" :fontSize ".75rem"
                                 :overflowWrap "anywhere"})}
        (str (:feed/url feed)
             (when-let [t (u/stamp (:feed/fetched feed))]
               (str " \u00b7 " t))))
     (when-let [e (:feed/error feed)]
       ($ mui/Alert {:severity "warning" :variant "outlined"
                     :sx (sx {:marginTop ".25rem"})}
          e))))

(def-ui Feeds
  "Everything somebody is subscribed to, as a list of rows that each open the
  one feed. The sidebar is for reading, and a feed is neither renamed nor
  dropped while reading; this is where the collection is kept, so this is
  where both are done.

  [Remove all feeds] is at the foot of the list and not among the OPML
  buttons below it: the sentence belongs on the thing it is about, and the
  thing it is about is the list it is standing under. It asks first
  (`leg.dialogs/RemoveAllFeeds`) - it is the one press on this page that
  cannot be undone by pressing something else."
  [feeds busy?]
  ($ page/Section {:title (str "Feeds (" (count feeds) ")")}
     (if (empty? feeds)
       ($ mui/Typography {:sx (sx {:color "text.secondary" :fontSize ".85rem"})}
          "None yet.")
       ($ :<>
          (for [f (sort-by #(str/lower-case (or (:feed/title %) (:feed/url %))) feeds)]
            ($ FeedRow {:key (:feed/id f) :feed f}))
          ($ mui/Button {:color "error"
                         :disabled busy?
                         :sx (sx {:marginTop ".5rem"})
                         :onClick (fn [_] (state/dialog! :remove-all-feeds))}
             "Remove all feeds")))))

(def-ui Collection
  "The whole collection, in and out. Both are OPML, which is what every
  reader speaks, and they are one section because they are one thought: this
  is where the feeds came from and this is how they leave."
  [busy?]
  (let [[result set-result] (kc/use-state nil)
        take-file!
        (fn [^js file]
          (when file
            ;; The file is read here and its text goes in an EDN body. This
            ;; API has one content type, and a multipart parser earns its
            ;; place in a program with more than one upload.
            (let [reader (js/FileReader.)]
              (set! (.-onload reader)
                    (fn [_]
                      (-> (state/import-opml! (.-result reader))
                          (.then (fn [answer] (when answer (set-result answer)))))))
              (.readAsText reader file))))]
    ($ page/Section {:title "Collection"}
       ($ mui/Typography {:sx (sx {:color "text.secondary" :fontSize ".85rem"})}
          "An OPML file, as Tiny Tiny RSS and every other reader reads and writes it. Importing leaves feeds already here alone.")
       ($ mui/Box {:sx (sx {:display "flex" :flexWrap "wrap" :alignItems "center"
                            :gap ".75rem" :marginTop ".5rem"})}
          ($ :input {:type "file"
                     :accept ".opml,.xml,text/xml,application/xml,text/x-opml"
                     :disabled busy?
                     :onChange (fn [^js e]
                                 (take-file! (aget (-> e .-target .-files) 0)))})
          ($ mui/Button {:disabled busy?
                         :onClick (fn [_] (state/export-opml!))}
             "Export"))
       (when result
         ($ mui/Alert {:severity "success" :variant "outlined"
                       :sx (sx {:marginTop ".5rem"})}
            (str (:added result) " added, " (:known result) " already here."))))))

;;; -------------------------------------------------------------- the page

(def-ui SettingsPane []
  (let [{:keys [feeds settings version busy?]} (state/use-state)
        theme (or (:reader/theme settings) "system")
        face  (theme/face (:reader/font settings))]
    ($ page/Page {:title "Settings"}
       ($ mui/Box {:sx (sx {:display "flex" :flexDirection "column" :gap "1.25rem"
                            :paddingTop ".75rem"})}
          ;; The address is shown and not offered for editing. It is the
          ;; account - everything in this reader hangs off it, and the
          ;; server refuses to change it (see leser.api/post-settings).
          ($ page/Section {:title "Signed in as"}
             ($ mui/Typography (str (:reader/email settings))))

          ;; Two rows, one under the other: the colours the page is in and
          ;; the type it is set in are the same kind of question, and both
          ;; are answered in one press.
          ($ page/Section {:title "Appearance"}
             ($ mui/Box {:sx (sx {:display "flex" :flexDirection "column"
                                  :alignItems "flex-start" :gap ".15rem"})}
                ($ Choice {:value theme
                           :options [["system" "System"] ["light" "Light"] ["dark" "Dark"]]
                           :on-pick (fn [k] (state/save-settings! {:reader/theme k}))})
                ;; Serif is the newspaper this reader is set as; sans-serif
                ;; is the same page in the face the buttons are already in,
                ;; for somebody who does not read a serif on a screen.
                ;; Mono is the third answer to the same question and the
                ;; only one that fetches a font (`leg.theme/faces`) - said
                ;; nowhere on the button, because a hundred kilobytes once
                ;; is not a decision anybody needs warning about.
                ($ Choice {:value face
                           :options [["serif" "Serif"] ["sans" "Sans-Serif"]
                                     ["mono" "Mono"]]
                           :on-pick (fn [k] (state/save-settings! {:reader/font k}))})))

          ;; Not under Appearance: this is not how the page looks but what
          ;; is in it. Oldest first is somebody catching up - the server
          ;; turns the list round before it cuts it to length, so that is
          ;; the oldest of what they have, not the newest of it backwards
          ;; (see `leser.api/get-articles`).
          ($ page/Section {:title "Article order"}
             ($ Choice {:value (if (u/oldest-first? (:reader/order settings))
                                 "oldest" "newest")
                        :options [["newest" "Newest first"] ["oldest" "Oldest first"]]
                        :on-pick (fn [k] (state/save-settings! {:reader/order k}))}))

          ;; Beside the order and for the same reason: a picture per row is
          ;; what the list holds, not how the page looks. A real answer
          ;; either way - a column of pictures is skimmed and a column of
          ;; headlines is read, and the pictures are fetched from the
          ;; publisher, which is somebody else's server and the reader's
          ;; own data allowance.
          ($ page/Section {:title "Pictures in the list"}
             ($ Choice {:value (if (u/images? (:reader/images settings)) "on" "off")
                        :options [["on" "Show"] ["off" "Hide"]]
                        :on-pick (fn [k] (state/save-settings! {:reader/images k}))}))

          ;; And beside both, because it is the third question of the same
          ;; kind: not how the page looks but what is on it when it opens.
          ;; A narrow window's question - a wide one shows the feeds beside
          ;; the articles, so both answers are the same screen - and said
          ;; rather than hidden on a wide screen, because the settings of a
          ;; reader are read on whatever is to hand and apply to the phone
          ;; it is read on (the same reason the keys are shown to
          ;; everybody).
          ($ page/Section {:title "Opens on"}
             ($ Choice {:value (if (u/start-feeds? (:reader/start settings))
                                 "feeds" "articles")
                        :options [["articles" "All articles"] ["feeds" "Home"]]
                        :on-pick (fn [k] (state/save-settings! {:reader/start k}))})
             ($ mui/Typography {:sx (sx {:color "text.secondary" :fontSize ".85rem"
                                         :marginTop ".25rem"})}
                "Where a window with no feed or article in its address comes up. On a wide screen both are the same page."))

          ($ AddFeed {:busy? busy?})
          ($ Feeds {:feeds feeds :busy? busy?})
          ($ Collection {:busy? busy?})
          ($ Keys {:settings settings})

          ;; Every device that was let in, and the one way to take a key
          ;; away again: a phone that was lost is signed out here.
          ($ page/Section {:title "Devices"}
             (for [c (:reader/clients settings)]
               ($ mui/Box {:key (str (:client/id c))
                           :sx (sx {:display "flex" :alignItems "center" :gap ".5rem"
                                    :padding ".25rem 0"})}
                  ($ :span {:style {:flex "1" :minWidth 0}}
                     (str (:client/name c)))
                  ($ :span {:style {:flex "none" :fontSize ".8rem" :opacity ".7"}}
                     (or (u/ago (:client/seen c)) ""))
                  ($ mui/Button {:color "error"
                                 :onClick (fn [_] (state/remove-client! (:client/id c)))}
                     "Sign out"))))

          ;; Only for the one address the server has in its code. Hidden
          ;; rather than disabled for everybody else: a button that tells
          ;; somebody there is a room they may not enter is worse than no
          ;; button, and the route behind it is refused anyway.
          (when (:reader/admin? settings)
            ($ page/Section {:title "This server"}
               ($ mui/Button {:variant "outlined"
                              :onClick (fn [_] (state/open-admin!))}
                  "Readers")))

          ($ mui/Box {:sx (sx {:display "flex" :alignItems "center" :gap ".5rem"})}
             ($ mui/Typography {:sx (sx {:color "text.secondary" :fontSize ".8rem"})}
                (str "Leser " (or version "dev")))
             ($ :span {:style {:flex "1"}})
             ($ mui/Button {:onClick (fn [_] (state/sign-out!))}
                "Sign out here"))))))
